import { useMemo } from 'react';
import type { ScaleTime } from 'd3-scale';
import type { Period } from '@/utilities/timeSeriesPeriodMap';
import {
  filterValuations,
  type Valuation,
} from '../../../utils/filterValuations';
import { createValuationsMap } from '../../../utils/createValuationsMap';
interface Props {
  keys: number[];
  xScale: ScaleTime<number, number>;
  valuations: Valuation[] | null | undefined;
  period: Period;
}

/**
 * Hook that manages valuation history for the narrative chart.
 * Handles filtering valuations by period, creating a time-based map,
 * and finding the closest valuation for any given timestamp.
 */
export const useNarrativeValuationHistory = ({
  keys,
  valuations,
  xScale,
  period,
}: Props) => {
  // Filter valuations to only include those within the selected period
  const filteredValuations = useMemo(
    () => filterValuations(valuations, period),
    [valuations, period]
  );

  // Create a mapping of valuations by date and generate timestamps
  const { valuationsByDate, valuationTimestamps } = useMemo(() => {
    const [domainStart, domainEnd] = xScale.domain().map((d) => d.getTime());
    return createValuationsMap(
      filteredValuations,
      keys,
      domainStart,
      domainEnd
    );
  }, [filteredValuations, xScale, keys]);

  return {
    valuationsByDate,
    valuationTimestamps,
  };
};
